import { LocalPage } from '@mytypes/local';
import { QUESTION_TYPE, SubmitCharacteristicsValuesRequestPayload, SubmitsCharacteristicUploadRequestPayload } from '@mytypes/protocol';
import {
  completeStepApi,
  deleteCharacteristicValueApi,
  submitCharacteristicsValuesApi,
  submitsCharacteristicUploadApi,
  submitUpdateUploadDescriptionsApi
} from '@services/protocol';
import { deleteFieldVisitWithDexie, getFieldVisitWithDexie, markUploadsAsUploadedWithDexie } from '@services/local';
import { parseCharacterizationValueToSubmit } from './parseCharacterizationValueToSubmit';
import { restoreSavedValuesInLocalVisit } from './restoreSavedValuesInLocalVisit';
import { AppDispatch } from '@state-mgmt/store';
import { setSaveInProgress } from '@state-mgmt/slices/appSlice';

const hasQuestionChanged = (characterizationId: string, newValue: any, latitude: number | undefined, longitude: number | undefined, savedValues: any[]) => {
  const value = savedValues.find(savedValue => savedValue.characterization_id === characterizationId);
  return value.value !== newValue || (value.latitude !== latitude && value.longitude !== longitude);
};

type CharacteristicToUpdate = {
  characterization_id: string;
  values: (string | number | boolean)[];
  latitude: number | undefined;
  longitude: number | undefined;
};

export const submitLocalFieldVisit = async (
  localId: number,
  removeFromLocal: boolean,
  dispatch: AppDispatch,
  onUploadImage?: (localId: string, valueId: string, url: string) => void
) => {
  dispatch(setSaveInProgress(true));
  try {
    const visit = await getFieldVisitWithDexie(localId);
    if (!visit) {
      dispatch(setSaveInProgress(false));
      return { success: false };
    }
    const complete = visit?.complete !== false;

    if (!visit.savedValues.length) {
      await restoreSavedValuesInLocalVisit(visit);
    }

    const characterizationsToUpdate: CharacteristicToUpdate[] = [];
    const characterizationsToDelete: any[] = [];

    visit.pages.forEach(({ questions }) => {
      questions.forEach(({ characterization_id, value, valueId, valueType, latitude, longitude }) => {
        if (valueType !== QUESTION_TYPE.UPLOAD && hasQuestionChanged(characterization_id, value, latitude, longitude, visit.savedValues)) {
          if (value) {
            characterizationsToUpdate.push({
              characterization_id,
              values: parseCharacterizationValueToSubmit(value, valueType),
              latitude,
              longitude
            });
          } else {
            valueId.forEach((vId: any) => {
              characterizationsToDelete.push({ characterization_id, vId });
            });
          }
        }
      });
    });

    const fieldDataPayload: SubmitCharacteristicsValuesRequestPayload = {
      characterizations: characterizationsToUpdate,
      complete,
      fieldId: visit.fieldId,
      protocolId: visit.protocolId,
      stepId: visit.stepId,
      trialId: visit.trialId
    };

    const updateDescriptions: { description: string; id: string }[] = [];

    const uploads: (SubmitsCharacteristicUploadRequestPayload & { localId: string | null | undefined })[] = [];
    visit.pages.forEach((page: LocalPage) => {
      page.questions
        .filter(({ value, valueType }) => value && valueType === QUESTION_TYPE.UPLOAD)
        .forEach(({ characterization_id, value }) => {
          // @ts-ignore
          value.forEach(({ image, latitude, longitude, valueId, remove, id, description }) => {
            const newUpload = !!image && !(visit.completedUploads || []).includes(id);
            const needsDeleted = !!valueId && !!remove;
            if (newUpload || needsDeleted) {
              uploads.push({
                characterizationId: characterization_id,
                fieldId: visit.fieldId,
                latitude,
                longitude,
                protocolId: visit.protocolId,
                remove,
                stepId: visit.stepId,
                trialId: visit.trialId,
                value: image,
                valueId,
                localId: id,
                description: description || null
              });
            } else if (!!valueId) {
              let match: { valueId: string; description: string | null | undefined } | null | undefined;
              visit.savedValues.forEach(sv => {
                if (Array.isArray(sv.value)) {
                  sv.value.forEach((v: any) => {
                    if (v.valueId === valueId) {
                      match = v;
                    }
                  });
                }
              });
              if (!match || (match?.description || '') !== (description || '')) {
                updateDescriptions.push({
                  id: valueId,
                  description: description || ''
                });
              }
            }
          });
        });
    });

    if (characterizationsToUpdate.length) {
      await submitCharacteristicsValuesApi(fieldDataPayload);
    }
    if (updateDescriptions.length) {
      await submitUpdateUploadDescriptionsApi({
        descriptions: updateDescriptions,
        fieldId: visit.fieldId,
        protocolId: visit.protocolId,
        stepId: visit.stepId,
        trialId: visit.trialId
      });
    }

    if (characterizationsToDelete.length) {
      const promises = characterizationsToDelete.map((item: any) => {
        if (item.characterization_id) {
          return new Promise(resolve =>
            resolve(
              deleteCharacteristicValueApi({
                characterizationId: item.characterization_id,
                fieldId: visit.fieldId,
                protocolId: visit.protocolId,
                stepId: visit.stepId,
                trialId: visit.trialId,
                valueId: item.vId
              })
            )
          );
        }
      });
      await Promise.all(promises);
    }

    const localUploadsCompleted: string[] = [];
    try {
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        if (upload.valueId && upload.remove) {
          await deleteCharacteristicValueApi(upload);
        } else {
          const uploadResponse = await submitsCharacteristicUploadApi(upload);
          if (upload.localId) {
            if (onUploadImage && uploadResponse?.id && uploadResponse.url) {
              onUploadImage(upload.localId, uploadResponse.id, uploadResponse.url);
            }
            localUploadsCompleted.push(upload.localId);
          }
        }
      }
    } catch (error) {
      console.error('An error occurred during upload', error);
      throw error;
    } finally {
      if (localUploadsCompleted.length) {
        await markUploadsAsUploadedWithDexie(localId, localUploadsCompleted);
      }
    }

    if (complete) {
      await completeStepApi({
        trialId: visit.trialId,
        stepId: visit.stepId,
        protocolId: visit.protocolId,
        fieldId: visit.fieldId,
        clientId: visit.clientId
      });
    }
    if (complete || removeFromLocal) {
      await deleteFieldVisitWithDexie(visit.id);
    }
    dispatch(setSaveInProgress(false));
    return { success: true, visit };
  } catch (error) {
    dispatch(setSaveInProgress(false));
    console.error('An error occurred submitLocalFieldVisit', error);
    return { success: false };
  }
};
